import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Button, Card, Col, Dropdown, Empty, Grid, Input, Row, Select, Spin, Table, Tag, Tooltip,
} from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ShopOutlined,
  StopOutlined,
  SyncOutlined,
  TagsOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import axios from '../../utils/axios';
import { showApiError } from '../../utils/apiError';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { palette } from '../../theme';
import {
  FilterBar,
  PageHeader,
  StatCard,
  StatGrid,
  StatusTag,
  TableCard,
  paginationDefaults,
  tableDefaults,
} from '../../components/admin';

// ---------------------------------------------------------------------------
// Chart colours — the only colour literals on this page
// ---------------------------------------------------------------------------

/**
 * Categorical series, led by the Vouchain indigo. Each colour keeps one
 * meaning across the page: indigo = voucher SKUs, teal = brands,
 * sky = discount, violet = markup, slate = offline SKUs.
 */
const CHART_SERIES = {
  indigo: '#5b5bd6',
  sky: '#0ea5e9',
  emerald: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  violet: '#8b5cf6',
  teal: '#14b8a6',
  slate: '#64748b',
};

/** Grid, axes and labels take the shared palette so charts sit quietly on white cards. */
const CHART_CHROME = {
  grid: palette.borderSoft,
  axis: palette.border,
  tick: palette.textSecondary,
  label: palette.text,
  labelOnBar: '#ffffff',
  cursor: palette.primarySoft,
  catchAll: palette.warning,
};

const X_AXIS_PROPS = {
  tick: { fontSize: 12, fill: CHART_CHROME.tick },
  tickLine: false,
  axisLine: { stroke: CHART_CHROME.axis },
};

const Y_AXIS_PROPS = {
  tick: { fontSize: 12, fill: CHART_CHROME.tick },
  tickLine: false,
  axisLine: false,
};

/** Counts in tables: aligned digits, normal weight (cell-money is for money). */
const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const BAR_RADIUS_UP = [4, 4, 0, 0];
const BAR_RADIUS_RIGHT = [0, 4, 4, 0];

/** App header height (theme Layout.headerHeight): where the sticky filter bar stops. */
const STICKY_TOP = 56;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Countries that are non-standard catch-all values in the source data. */
const CATCH_ALL_COUNTRIES = new Set(['Other', 'Global']);

/**
 * External vouchers get their catalog expiry recomputed from "now" on every
 * sync (the provider reports a redemption window, not a shelf life), so the
 * expiry buckets above can never flag a genuinely at-risk external SKU — the
 * only thing that goes stale is the sync itself. These buckets surface that.
 */
const SYNC_AGE_BUCKETS = [
  { id: 'fresh', name: 'Synced today', tone: 'success', icon: <CheckCircleOutlined />, matches: (days) => days != null && days <= 1 },
  { id: 'week', name: '2–7 days ago', tone: 'success', icon: <SyncOutlined />, matches: (days) => days != null && days > 1 && days <= 7 },
  { id: 'month', name: '8–30 days ago', tone: 'warning', icon: <ClockCircleOutlined />, matches: (days) => days != null && days > 7 && days <= 30 },
  { id: 'stale', name: '30+ days ago', tone: 'danger', urgent: true, icon: <WarningOutlined />, matches: (days) => days != null && days > 30 },
  { id: 'never', name: 'Never synced', tone: 'default', icon: <StopOutlined />, matches: (days) => days == null },
];

const EXPIRY_BUCKETS = [
  { id: 'expired', name: 'Expired', tone: 'danger', urgent: true, icon: <ExclamationCircleOutlined />, matches: (days) => days < 0 },
  { id: 'within-30', name: '≤30 days', tone: 'danger', urgent: true, icon: <FieldTimeOutlined />, matches: (days) => days >= 0 && days <= 30 },
  { id: '31-60', name: '31–60 days', tone: 'warning', icon: <ClockCircleOutlined />, matches: (days) => days >= 31 && days <= 60 },
  { id: '61-90', name: '61–90 days', tone: 'warning', icon: <ClockCircleOutlined />, matches: (days) => days >= 61 && days <= 90 },
  { id: 'over-90', name: '90+ days', tone: 'success', icon: <CheckCircleOutlined />, matches: (days) => days > 90 },
];

const spreadsheetSafeValue = (value) => (
  typeof value === 'string' && /^[=+\-@]/.test(value) ? `'${value}` : value
);

// ---------------------------------------------------------------------------
// On-chart label renderers (values must always be visible, never hover-only)
// ---------------------------------------------------------------------------

/** Centered white label inside a stacked segment; hidden when the segment is
 * too small (<16px) to hold legible text. */
const renderStackedLabel = (formatter) => (props) => {
  const { x, y, width, height, value } = props;
  if (value == null || value === 0 || height < 16 || width < 16) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill={CHART_CHROME.labelOnBar}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {formatter ? formatter(value) : value}
    </text>
  );
};

/** Dark label above a plain (non-stacked) bar. */
const renderTopLabel = (formatter) => (props) => {
  const { x, y, width, value } = props;
  if (value == null) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      fill={CHART_CHROME.label}
      textAnchor="middle"
      fontSize={12}
      fontWeight={500}
    >
      {formatter ? formatter(value) : value}
    </text>
  );
};

const pct1 = (v) => `${Number(v).toFixed(1)}%`;
const skus = (v) => `${Number(v).toLocaleString()} SKUs`;
const geoValue = (v, item) => (item.dataKey === 'brands' ? `${Number(v).toLocaleString()} brands` : skus(v));

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const TOOLTIP_STYLE = {
  background: 'var(--c-surface)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  boxShadow: 'var(--shadow-raised)',
  padding: '8px 10px',
  fontSize: 12,
  minWidth: 150,
};

/** Hover card for every chart: category, then one row per series with its unit. */
const ChartTip = ({ active, payload, label, format }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={TOOLTIP_STYLE}>
      <div style={{ fontWeight: 600, color: 'var(--c-text)', marginBottom: 4 }}>{label}</div>
      {payload.map((item) => (
        <div
          key={item.dataKey}
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text-2)', lineHeight: '20px' }}
        >
          <span
            aria-hidden="true"
            style={{ width: 8, height: 8, borderRadius: 2, background: item.color || item.fill, flex: '0 0 auto' }}
          />
          <span>{item.name}</span>
          <span
            style={{
              marginLeft: 'auto',
              paddingLeft: 12,
              fontWeight: 600,
              color: 'var(--c-text)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {format ? format(item.value, item) : item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const chartTooltip = (format) => (
  <ChartTooltip cursor={{ fill: CHART_CHROME.cursor }} content={<ChartTip format={format} />} />
);

/** White card with a title row; `unit` sits on the right of the title. */
const ChartCard = ({ title, unit, height = 280, empty = false, footnote, children }) => (
  <Card
    className="admin-card"
    title={title}
    extra={unit ? <span className="cell-sub">{unit}</span> : null}
    styles={{ header: { padding: '0 16px' }, body: { padding: '12px 16px' } }}
  >
    {empty ? (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No vouchers match these filters" />
      </div>
    ) : (
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    )}
    {footnote ? <div className="cell-sub" style={{ marginTop: 8 }}>{footnote}</div> : null}
  </Card>
);

/** A group of the page (catalogue, pricing, …): small heading, optional note and control. */
const Group = ({ title, note, extra, first = false, children }) => (
  <section style={first ? undefined : { marginTop: 28 }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        margin: '4px 0 10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
        <h2 className="group-heading" style={{ margin: 0 }}>{title}</h2>
        {note ? <span className="cell-sub">{note}</span> : null}
      </div>
      {extra}
    </div>
    {children}
  </section>
);

/** Tile label with an info icon that explains the definition. */
const TipLabel = ({ label, tip }) => (
  <span>
    {label}{' '}
    <Tooltip title={tip}>
      <InfoCircleOutlined className="muted" style={{ fontSize: 12 }} />
    </Tooltip>
  </span>
);

const VOUCHER_STATUS_TIP = 'Listing/sales state: online = eligible for the sales flow (still subject to isActive, expiry and stock checks); offline = delisted but retained.';
const ACTIVE_TIP = 'Record lifecycle validity, independent of expiry: inactive = soft-deleted, retired, or no longer offered by the provider.';

const syncAgeTone = (ageDays) => (ageDays > 30 ? 'danger' : ageDays > 7 ? 'warning' : 'success');
const coverageTone = (pct) => (pct === 0 ? 'danger' : pct < 100 ? 'warning' : 'success');
const daysLeftTone = (d) => (d < 0 || d <= 7 ? 'danger' : d <= 90 ? 'warning' : 'success');

// ---------------------------------------------------------------------------
// Aggregation helpers
// ---------------------------------------------------------------------------

const distinctCount = (rows, key, normalize) => {
  const set = new Set();
  rows.forEach((r) => {
    const raw = r[key];
    if (raw == null || raw === '') return;
    set.add(normalize ? normalize(raw) : raw);
  });
  return set.size;
};

const groupCount = (rows, keyFn) => {
  const map = new Map();
  rows.forEach((r) => {
    const k = keyFn(r);
    if (k == null) return;
    map.set(k, (map.get(k) || 0) + 1);
  });
  return map;
};

const groupAvg = (rows, keyFn, valueFn) => {
  const map = new Map();
  rows.forEach((r) => {
    const k = keyFn(r);
    const v = valueFn(r);
    if (k == null || v == null || !Number.isFinite(v)) return;
    const cur = map.get(k) || { sum: 0, n: 0 };
    cur.sum += v;
    cur.n += 1;
    map.set(k, cur);
  });
  const out = new Map();
  map.forEach((v, k) => out.set(k, v.sum / v.n));
  return out;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SupplyDashboard = () => {
  const screens = Grid.useBreakpoint();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);

  const [filterProviders, setFilterProviders] = useState([]);
  const [filterCountries, setFilterCountries] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [expirySearch, setExpirySearch] = useState('');
  const [selectedExpiryBucket, setSelectedExpiryBucket] = useState('within-30');
  const expiryTableRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/admin/supply-dashboard');
      setSnapshot(res.data);
    } catch (e) {
      showApiError(e, 'Failed to load supply snapshot');
      setError(e?.response?.data?.message || e.message || 'Failed to load supply snapshot');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);

  const vouchers = useMemo(() => snapshot?.vouchers || [], [snapshot]);
  const snapshotTime = useMemo(
    () => (snapshot?.generatedAt ? dayjs(snapshot.generatedAt) : null),
    [snapshot],
  );

  const filterOptions = useMemo(() => {
    const opt = (values) => [...values].sort().map((v) => ({ value: v, label: v }));
    return {
      providers: opt(new Set(vouchers.map((v) => v.provider))),
      countries: opt(new Set(vouchers.map((v) => v.country))),
      categories: opt(new Set(vouchers.map((v) => v.category))),
      statuses: [{ value: 'online', label: 'Online' }, { value: 'offline', label: 'Offline' }],
    };
  }, [vouchers]);

  const filtered = useMemo(() => vouchers.filter((v) => (
    (filterProviders.length === 0 || filterProviders.includes(v.provider))
    && (filterCountries.length === 0 || filterCountries.includes(v.country))
    && (filterCategories.length === 0 || filterCategories.includes(v.category))
    && (filterStatuses.length === 0 || filterStatuses.includes(v.voucherStatus))
  )), [vouchers, filterProviders, filterCountries, filterCategories, filterStatuses]);

  // ---- Catalog overview -----------------------------------------------------

  const kpis = useMemo(() => ({
    suppliers: distinctCount(filtered, 'provider'),
    brands: distinctCount(filtered, 'brand', (b) => String(b).trim().toLowerCase()),
    skus: filtered.length,
    countries: distinctCount(filtered, 'country'),
  }), [filtered]);

  const statusStats = useMemo(() => {
    const total = filtered.length || 1;
    const online = filtered.filter((v) => v.voucherStatus === 'online').length;
    const active = filtered.filter((v) => v.isActive).length;
    const pc = (n) => `${((n / total) * 100).toFixed(1)}%`;
    return {
      status: [
        { label: 'Online', value: online, pct: pc(online) },
        { label: 'Offline', value: filtered.length - online, pct: pc(filtered.length - online) },
      ],
      active: [
        { label: 'Active', value: active, pct: pc(active) },
        { label: 'Inactive', value: filtered.length - active, pct: pc(filtered.length - active) },
      ],
    };
  }, [filtered]);

  const skusBySupplier = useMemo(() => {
    const m = groupCount(filtered, (v) => v.provider);
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  }, [filtered]);

  const skusByCategory = useMemo(() => {
    const m = groupCount(filtered, (v) => v.category);
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  }, [filtered]);

  // ---- Geographic breakdown -------------------------------------------------

  const geoByCountry = useMemo(() => {
    const countries = new Map();
    filtered.forEach((v) => {
      const cur = countries.get(v.country) || { brands: new Set(), online: 0, offline: 0 };
      cur.brands.add(String(v.brand).trim().toLowerCase());
      if (v.voucherStatus === 'online') cur.online += 1; else cur.offline += 1;
      countries.set(v.country, cur);
    });
    return [...countries.entries()]
      .map(([name, c]) => ({
        name,
        brands: c.brands.size,
        online: c.online,
        offline: c.offline,
        total: c.online + c.offline,
        catchAll: CATCH_ALL_COUNTRIES.has(name),
      }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  // ---- Pricing & margin -----------------------------------------------------

  const discountBuckets = useMemo(() => {
    const buckets = [
      { name: 'Negative', count: 0 },
      { name: 'Exactly 0%', count: 0 },
      { name: '0–10%', count: 0 },
      { name: '10–25%', count: 0 },
      { name: '25–50%', count: 0 },
      { name: '50%+', count: 0 },
    ];
    filtered.forEach((v) => {
      const d = v.discountRate;
      if (d == null || !Number.isFinite(d)) return;
      if (d < 0) buckets[0].count += 1;
      else if (d === 0) buckets[1].count += 1;
      else if (d <= 10) buckets[2].count += 1;
      else if (d <= 25) buckets[3].count += 1;
      else if (d <= 50) buckets[4].count += 1;
      else buckets[5].count += 1;
    });
    return buckets;
  }, [filtered]);

  const markupBySupplier = useMemo(() => {
    const m = groupAvg(filtered, (v) => v.provider, (v) => v.markupRate);
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([name, avg]) => ({ name, avg }));
  }, [filtered]);

  const discountByCountry = useMemo(() => {
    const m = groupAvg(filtered, (v) => v.country, (v) => v.discountRate);
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([name, avg]) => ({ name, avg }));
  }, [filtered]);

  const markupByCountry = useMemo(() => {
    const m = groupAvg(filtered, (v) => v.country, (v) => v.markupRate);
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([name, avg]) => ({ name, avg }));
  }, [filtered]);

  // ---- Expiry & risk ----------------------------------------------------------

  const nowMs = useMemo(
    () => (snapshotTime ? snapshotTime.valueOf() : Date.now()),
    [snapshotTime],
  );

  const daysLeft = useCallback((v) => {
    if (!v.expiryEndDatetime) return null;
    return Math.ceil((new Date(v.expiryEndDatetime).getTime() - nowMs) / DAY_MS);
  }, [nowMs]);

  const expiryBuckets = useMemo(() => {
    const buckets = EXPIRY_BUCKETS.map((bucket) => ({ ...bucket, count: 0 }));
    filtered.forEach((v) => {
      const d = daysLeft(v);
      if (d == null) return;
      const bucket = buckets.find((item) => item.matches(d));
      if (bucket) bucket.count += 1;
    });
    return buckets;
  }, [filtered, daysLeft]);

  const activeExpiryBucket = useMemo(
    () => expiryBuckets.find((bucket) => bucket.id === selectedExpiryBucket) || expiryBuckets[1],
    [expiryBuckets, selectedExpiryBucket],
  );

  const expiryRows = useMemo(() => {
    const q = expirySearch.trim().toLowerCase();
    return filtered
      .map((v) => ({ ...v, daysLeft: daysLeft(v) }))
      .filter((v) => v.daysLeft != null && activeExpiryBucket.matches(v.daysLeft))
      .filter((v) => !q
        || String(v.name || '').toLowerCase().includes(q)
        || String(v.provider || '').toLowerCase().includes(q));
  }, [filtered, daysLeft, activeExpiryBucket, expirySearch]);

  const selectExpiryBucket = useCallback((bucketId) => {
    setSelectedExpiryBucket(bucketId);
    setExpirySearch('');
    window.requestAnimationFrame(() => {
      expiryTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // ---- Supplier sync freshness ----------------------------------------------

  /** Only external vouchers are synced; internal ones have no upstream. */
  const externalRows = useMemo(
    () => filtered.filter((v) => v.isExternalVoucher),
    [filtered],
  );

  const syncAgeDays = useCallback((v) => {
    if (!v.lastSyncDate) return null;
    return Math.floor((nowMs - new Date(v.lastSyncDate).getTime()) / DAY_MS);
  }, [nowMs]);

  const syncAgeBuckets = useMemo(() => {
    const buckets = SYNC_AGE_BUCKETS.map((bucket) => ({ ...bucket, count: 0 }));
    externalRows.forEach((v) => {
      const bucket = buckets.find((item) => item.matches(syncAgeDays(v)));
      if (bucket) bucket.count += 1;
    });
    return buckets;
  }, [externalRows, syncAgeDays]);

  /** Newest sync per supplier — what an operator actually acts on. */
  const supplierFreshness = useMemo(() => {
    const bySupplier = new Map();
    externalRows.forEach((v) => {
      const ts = v.lastSyncDate ? new Date(v.lastSyncDate).getTime() : null;
      const cur = bySupplier.get(v.provider) || { skus: 0, newest: null, oldest: null, unset: 0 };
      cur.skus += 1;
      if (ts == null) cur.unset += 1;
      else {
        cur.newest = cur.newest == null ? ts : Math.max(cur.newest, ts);
        cur.oldest = cur.oldest == null ? ts : Math.min(cur.oldest, ts);
      }
      bySupplier.set(v.provider, cur);
    });
    return [...bySupplier.entries()]
      .map(([provider, c]) => ({
        provider,
        skus: c.skus,
        unset: c.unset,
        newest: c.newest,
        oldest: c.oldest,
        ageDays: c.newest == null ? null : Math.floor((nowMs - c.newest) / DAY_MS),
      }))
      .sort((a, b) => (b.ageDays ?? Infinity) - (a.ageDays ?? Infinity));
  }, [externalRows, nowMs]);

  /** How many external SKUs carry a real post-purchase redemption window. */
  const redemptionCoverage = useMemo(() => {
    const withWindow = externalRows.filter((v) => v.redemptionValidityDays != null);
    const bySupplier = new Map();
    externalRows.forEach((v) => {
      const cur = bySupplier.get(v.provider) || { total: 0, set: 0 };
      cur.total += 1;
      if (v.redemptionValidityDays != null) cur.set += 1;
      bySupplier.set(v.provider, cur);
    });
    return {
      total: externalRows.length,
      set: withWindow.length,
      bySupplier: [...bySupplier.entries()]
        .map(([provider, c]) => ({ provider, ...c }))
        .sort((a, b) => b.total - a.total),
    };
  }, [externalRows]);

  const exportExpiryRows = useCallback((format) => {
    const rows = expiryRows.map((voucher) => ({
      'Voucher ID': spreadsheetSafeValue(voucher.id),
      Voucher: spreadsheetSafeValue(voucher.name),
      Brand: spreadsheetSafeValue(voucher.brand),
      Supplier: spreadsheetSafeValue(voucher.provider),
      Country: spreadsheetSafeValue(voucher.country),
      Category: spreadsheetSafeValue(voucher.category),
      'Voucher Status': voucher.voucherStatus,
      Active: voucher.isActive ? 'Yes' : 'No',
      'Risk Bucket': activeExpiryBucket.name,
      'Expiry Date': dayjs(voucher.expiryEndDatetime).format('YYYY-MM-DD'),
      'Days Left': voucher.daysLeft,
      Quantity: voucher.totalQuantity ?? '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 26 }, { wch: 34 }, { wch: 22 }, { wch: 18 },
      { wch: 16 }, { wch: 24 }, { wch: 16 }, { wch: 10 },
      { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expiry Risk');
    const timestamp = snapshotTime?.format('YYYYMMDD-HHmm') || dayjs().format('YYYYMMDD-HHmm');
    XLSX.writeFile(
      workbook,
      `expiry-risk-${activeExpiryBucket.id}-${timestamp}.${format}`,
      { bookType: format },
    );
  }, [activeExpiryBucket, expiryRows, snapshotTime]);

  const expiryColumns = useMemo(() => [
    {
      title: 'Voucher',
      dataIndex: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div>
          <div className="cell-main" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</div>
          <div className="cell-sub">{record.brand}</div>
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'provider',
      width: 110,
      sorter: (a, b) => a.provider.localeCompare(b.provider),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      width: 110,
      sorter: (a, b) => a.country.localeCompare(b.country),
      render: (c) => (CATCH_ALL_COUNTRIES.has(c) ? (
        <Tooltip title="Non-standard catch-all value in the source data">
          <span><StatusTag tone="warning">{c}</StatusTag></span>
        </Tooltip>
      ) : c),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 180,
      sorter: (a, b) => a.category.localeCompare(b.category),
    },
    {
      title: 'Expiry date',
      dataIndex: 'expiryEndDatetime',
      width: 130,
      sorter: (a, b) => new Date(a.expiryEndDatetime) - new Date(b.expiryEndDatetime),
      render: (d) => formatDate(d),
    },
    {
      title: 'Days left',
      dataIndex: 'daysLeft',
      width: 110,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.daysLeft - b.daysLeft,
      render: (d) => (
        <StatusTag tone={daysLeftTone(d)}>
          {d < 0 ? `${Math.abs(d)}d ago` : `${d}d`}
        </StatusTag>
      ),
    },
    {
      title: (
        <span>
          Qty{' '}
          <Tooltip title="totalQuantity defaults to a flat 100,000 for nearly all provider-synced SKUs and does not represent verified stock; it is never summed anywhere on this dashboard.">
            <InfoCircleOutlined className="muted" />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalQuantity',
      width: 140,
      align: 'right',
      sorter: (a, b) => (a.totalQuantity || 0) - (b.totalQuantity || 0),
      render: (q) => (q === 100000 ? (
        <span style={NUM_STYLE}>
          {q.toLocaleString()}
          <Tag bordered={false} style={{ marginInlineStart: 6, marginInlineEnd: 0 }}>default</Tag>
        </span>
      ) : (q == null ? '—' : <span style={NUM_STYLE}>{q.toLocaleString()}</span>)),
    },
  ], []);

  // ---- Render ---------------------------------------------------------------

  const filtersActive = filterProviders.length > 0
    || filterCountries.length > 0
    || filterCategories.length > 0
    || filterStatuses.length > 0;

  const resetFilters = () => {
    setFilterProviders([]);
    setFilterCountries([]);
    setFilterCategories([]);
    setFilterStatuses([]);
  };

  // Keep the filters in view while scrolling through the charts on wide
  // screens; on narrow ones the stacked selects would cover too much.
  const stickyFilters = !!screens.lg;

  const header = (
    <PageHeader
      title="Supply dashboard"
      subtitle="Catalogue coverage, pricing, supplier sync freshness and expiry risk. The filters apply to every figure on the page."
      extra={(
        <>
          {snapshotTime ? (
            <Tooltip title="A point-in-time snapshot, not a live feed.">
              <span className="cell-sub">Data as of {formatDateTime(snapshotTime)}</span>
            </Tooltip>
          ) : null}
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchSnapshot}>
            Refresh
          </Button>
        </>
      )}
    />
  );

  if (loading && !snapshot) {
    return (
      <div>
        {header}
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const categoryChartHeight = Math.max(280, skusByCategory.length * 28 + 32);
  const hasCatchAllCountry = geoByCountry.some((c) => c.catchAll);

  return (
    <div>
      {header}

      {error && (
        <Alert
          type="error"
          showIcon
          message="Supply dashboard failed to load"
          description={error}
          style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={fetchSnapshot}>Retry</Button>}
        />
      )}

      <div
        style={stickyFilters ? {
          position: 'sticky',
          top: STICKY_TOP,
          zIndex: 10,
          background: 'var(--c-bg)',
          paddingTop: 8,
          marginTop: -8,
        } : undefined}
      >
        <FilterBar onReset={resetFilters} resetDisabled={!filtersActive}>
          {[
            { key: 'supplier', placeholder: 'All suppliers', value: filterProviders, set: setFilterProviders, options: filterOptions.providers },
            { key: 'country', placeholder: 'All countries', value: filterCountries, set: setFilterCountries, options: filterOptions.countries },
            { key: 'category', placeholder: 'All categories', value: filterCategories, set: setFilterCategories, options: filterOptions.categories },
            { key: 'status', placeholder: 'All voucher statuses', value: filterStatuses, set: setFilterStatuses, options: filterOptions.statuses },
          ].map((f) => (
            <FilterBar.Item key={f.key} grow>
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                placeholder={f.placeholder}
                aria-label={f.placeholder}
                value={f.value}
                onChange={f.set}
                options={f.options}
              />
            </FilterBar.Item>
          ))}
        </FilterBar>
      </div>

      {/* ------------------------------ Catalogue overview --------------------- */}
      <Group title="Catalogue" first>
        <StatGrid min={180}>
          <StatCard label="Suppliers" value={kpis.suppliers.toLocaleString()} icon={<ShopOutlined />} />
          <StatCard label="Brands" value={kpis.brands.toLocaleString()} icon={<TagsOutlined />} />
          <StatCard
            label="Voucher SKUs"
            value={kpis.skus.toLocaleString()}
            icon={<AppstoreOutlined />}
            hint={filtersActive ? `of ${vouchers.length.toLocaleString()} in the snapshot` : 'All SKUs in the snapshot'}
          />
          <StatCard label="Countries" value={kpis.countries.toLocaleString()} icon={<GlobalOutlined />} />
        </StatGrid>
        <StatGrid min={180}>
          <StatCard
            label={<TipLabel label="Online" tip={VOUCHER_STATUS_TIP} />}
            value={statusStats.status[0].value.toLocaleString()}
            icon={<EyeOutlined />}
            tone="success"
            hint={`${statusStats.status[0].pct} of SKUs`}
          />
          <StatCard
            label={<TipLabel label="Offline" tip={VOUCHER_STATUS_TIP} />}
            value={statusStats.status[1].value.toLocaleString()}
            icon={<EyeInvisibleOutlined />}
            hint={`${statusStats.status[1].pct} of SKUs`}
          />
          <StatCard
            label={<TipLabel label="Active" tip={ACTIVE_TIP} />}
            value={statusStats.active[0].value.toLocaleString()}
            icon={<CheckCircleOutlined />}
            tone="success"
            hint={`${statusStats.active[0].pct} of SKUs`}
          />
          <StatCard
            label={<TipLabel label="Inactive" tip={ACTIVE_TIP} />}
            value={statusStats.active[1].value.toLocaleString()}
            icon={<StopOutlined />}
            hint={`${statusStats.active[1].pct} of SKUs`}
          />
        </StatGrid>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <ChartCard title="SKUs by supplier" unit="Voucher SKUs" empty={skusBySupplier.length === 0}>
              <BarChart data={skusBySupplier} margin={{ top: 24, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
                <XAxis dataKey="name" {...X_AXIS_PROPS} />
                <YAxis allowDecimals={false} {...Y_AXIS_PROPS} />
                {chartTooltip(skus)}
                <Bar dataKey="count" name="Vouchers" fill={CHART_SERIES.indigo} radius={BAR_RADIUS_UP} maxBarSize={48} isAnimationActive={false}>
                  <LabelList dataKey="count" content={renderTopLabel()} />
                </Bar>
              </BarChart>
            </ChartCard>
          </Col>
          <Col xs={24} xl={12}>
            <ChartCard
              title="SKUs by category"
              unit="Voucher SKUs"
              height={categoryChartHeight}
              empty={skusByCategory.length === 0}
            >
              <BarChart data={skusByCategory} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 0 }}>
                <CartesianGrid horizontal={false} stroke={CHART_CHROME.grid} />
                <XAxis type="number" allowDecimals={false} {...X_AXIS_PROPS} />
                <YAxis type="category" dataKey="name" width={160} interval={0} {...Y_AXIS_PROPS} />
                {chartTooltip(skus)}
                <Bar dataKey="count" name="Vouchers" fill={CHART_SERIES.indigo} radius={BAR_RADIUS_RIGHT} maxBarSize={20} isAnimationActive={false}>
                  <LabelList
                    dataKey="count"
                    position="right"
                    style={{ fill: CHART_CHROME.label, fontSize: 12, fontWeight: 500 }}
                  />
                </Bar>
              </BarChart>
            </ChartCard>
          </Col>
        </Row>
      </Group>

      {/* ---------------------------- Geographic breakdown --------------------- */}
      <Group title="By country">
        <ChartCard
          title="Brands and SKUs by country"
          unit="SKUs split online / offline"
          height={340}
          empty={geoByCountry.length === 0}
          footnote={hasCatchAllCountry
            ? '* "Other" and "Global" are non-standard catch-all values retained from the source data.'
            : null}
        >
          <BarChart data={geoByCountry} margin={{ top: 24, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={{ stroke: CHART_CHROME.axis }}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y + 14}
                  textAnchor="middle"
                  fontSize={12}
                  fill={CATCH_ALL_COUNTRIES.has(payload.value) ? CHART_CHROME.catchAll : CHART_CHROME.tick}
                >
                  {payload.value}{CATCH_ALL_COUNTRIES.has(payload.value) ? ' *' : ''}
                </text>
              )}
            />
            <YAxis allowDecimals={false} {...Y_AXIS_PROPS} />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ paddingBottom: 8 }}
              formatter={(value) => <span style={{ color: 'var(--c-text-2)', fontSize: 12 }}>{value}</span>}
            />
            {chartTooltip(geoValue)}
            <Bar dataKey="brands" name="Brands" fill={CHART_SERIES.teal} radius={BAR_RADIUS_UP} maxBarSize={40} isAnimationActive={false}>
              <LabelList dataKey="brands" content={renderTopLabel()} />
            </Bar>
            <Bar dataKey="online" name="Online SKUs" stackId="sku" fill={CHART_SERIES.indigo} maxBarSize={40} isAnimationActive={false}>
              <LabelList dataKey="online" content={renderStackedLabel()} />
            </Bar>
            <Bar dataKey="offline" name="Offline SKUs" stackId="sku" fill={CHART_SERIES.slate} radius={BAR_RADIUS_UP} maxBarSize={40} isAnimationActive={false}>
              <LabelList dataKey="offline" content={renderStackedLabel()} />
              <LabelList dataKey="total" position="top" style={{ fill: CHART_CHROME.label, fontSize: 12, fontWeight: 500 }} />
            </Bar>
          </BarChart>
        </ChartCard>
      </Group>

      {/* ------------------------------ Pricing & margin ----------------------- */}
      <Group title="Pricing and margin">
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <ChartCard
              title="Discount rate distribution"
              unit="SKUs by discount off face value"
              empty={discountBuckets.every((b) => b.count === 0)}
            >
              <BarChart data={discountBuckets} margin={{ top: 24, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
                <XAxis dataKey="name" {...X_AXIS_PROPS} />
                <YAxis allowDecimals={false} {...Y_AXIS_PROPS} />
                {chartTooltip(skus)}
                <Bar dataKey="count" name="Vouchers" fill={CHART_SERIES.sky} radius={BAR_RADIUS_UP} maxBarSize={48} isAnimationActive={false}>
                  <LabelList dataKey="count" content={renderTopLabel()} />
                </Bar>
              </BarChart>
            </ChartCard>
          </Col>
          <Col xs={24} xl={12}>
            <ChartCard title="Average markup by supplier" unit="% over cost" empty={markupBySupplier.length === 0}>
              <BarChart data={markupBySupplier} margin={{ top: 24, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
                <XAxis dataKey="name" {...X_AXIS_PROPS} />
                <YAxis tickFormatter={pct1} {...Y_AXIS_PROPS} />
                {chartTooltip(pct1)}
                <Bar dataKey="avg" name="Avg markup" fill={CHART_SERIES.violet} radius={BAR_RADIUS_UP} maxBarSize={48} isAnimationActive={false}>
                  <LabelList dataKey="avg" content={renderTopLabel(pct1)} />
                </Bar>
              </BarChart>
            </ChartCard>
          </Col>
          <Col xs={24} xl={12}>
            <ChartCard title="Average discount by country" unit="% off face value" empty={discountByCountry.length === 0}>
              <BarChart data={discountByCountry} margin={{ top: 24, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
                <XAxis dataKey="name" {...X_AXIS_PROPS} />
                <YAxis tickFormatter={pct1} {...Y_AXIS_PROPS} />
                {chartTooltip(pct1)}
                <Bar dataKey="avg" name="Avg discount" fill={CHART_SERIES.sky} radius={BAR_RADIUS_UP} maxBarSize={48} isAnimationActive={false}>
                  <LabelList dataKey="avg" content={renderTopLabel(pct1)} />
                </Bar>
              </BarChart>
            </ChartCard>
          </Col>
          <Col xs={24} xl={12}>
            <ChartCard title="Average markup by country" unit="% over cost" empty={markupByCountry.length === 0}>
              <BarChart data={markupByCountry} margin={{ top: 24, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
                <XAxis dataKey="name" {...X_AXIS_PROPS} />
                <YAxis tickFormatter={pct1} {...Y_AXIS_PROPS} />
                {chartTooltip(pct1)}
                <Bar dataKey="avg" name="Avg markup" fill={CHART_SERIES.violet} radius={BAR_RADIUS_UP} maxBarSize={48} isAnimationActive={false}>
                  <LabelList dataKey="avg" content={renderTopLabel(pct1)} />
                </Bar>
              </BarChart>
            </ChartCard>
          </Col>
        </Row>
      </Group>

      {/* --------------------------- Supplier sync freshness ------------------- */}
      <Group title="Supplier sync freshness" note={`${externalRows.length.toLocaleString()} external SKUs, by last sync`}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Why sync age matters more than expiry for external SKUs"
          description={
            'External suppliers report a redemption window, not a shelf life, so provider sync '
            + 'recomputes each catalogue expiry date from the moment it runs. That makes the expiry '
            + 'buckets below meaningless for external SKUs: they always sit 60–120 days out and reset on '
            + 'every sync. A stale sync is the real supply risk, because the catalogue stops reflecting '
            + 'what the supplier still sells.'
          }
        />
        <StatGrid min={160}>
          {syncAgeBuckets.map((b) => (
            <StatCard
              key={b.id}
              label={b.name}
              value={b.count.toLocaleString()}
              icon={b.icon}
              tone={b.tone}
              highlight={b.urgent && b.count > 0}
            />
          ))}
        </StatGrid>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <TableCard title="Last sync per supplier" count={supplierFreshness.length}>
              <Table
                {...tableDefaults}
                rowKey="provider"
                pagination={false}
                dataSource={supplierFreshness}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No external SKUs match these filters" /> }}
                columns={[
                  { title: 'Supplier', dataIndex: 'provider', render: (p) => <span className="cell-main">{p}</span> },
                  { title: 'SKUs', dataIndex: 'skus', align: 'right', width: 80, render: (n) => <span style={NUM_STYLE}>{n.toLocaleString()}</span> },
                  {
                    title: 'Last sync',
                    key: 'newest',
                    render: (_, r) => (r.newest ? formatDateTime(r.newest) : '—'),
                  },
                  {
                    title: 'Age',
                    key: 'age',
                    align: 'right',
                    width: 110,
                    render: (_, r) => (r.ageDays == null
                      ? <StatusTag>Never</StatusTag>
                      : <StatusTag tone={syncAgeTone(r.ageDays)}>{r.ageDays}d ago</StatusTag>),
                  },
                ]}
              />
            </TableCard>
          </Col>
          <Col xs={24} xl={12}>
            <TableCard
              title="Redemption window coverage"
              count={`${redemptionCoverage.set.toLocaleString()} / ${redemptionCoverage.total.toLocaleString()}`}
              toolbar={(
                <span className="cell-sub">
                  Days a member has to redeem after purchase. Only GoWabi reports this upstream; for other
                  suppliers set it per voucher in Voucher management.
                </span>
              )}
            >
              <Table
                {...tableDefaults}
                rowKey="provider"
                pagination={false}
                dataSource={redemptionCoverage.bySupplier}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No external SKUs match these filters" /> }}
                columns={[
                  { title: 'Supplier', dataIndex: 'provider', render: (p) => <span className="cell-main">{p}</span> },
                  { title: 'SKUs', dataIndex: 'total', align: 'right', width: 80, render: (n) => <span style={NUM_STYLE}>{n.toLocaleString()}</span> },
                  { title: 'Window set', dataIndex: 'set', align: 'right', width: 110, render: (n) => <span style={NUM_STYLE}>{n.toLocaleString()}</span> },
                  {
                    title: 'Coverage',
                    key: 'pct',
                    align: 'right',
                    width: 110,
                    render: (_, r) => {
                      const pct = r.total > 0 ? (r.set / r.total) * 100 : 0;
                      return <StatusTag tone={coverageTone(pct)}>{pct.toFixed(0)}%</StatusTag>;
                    },
                  },
                ]}
              />
            </TableCard>
          </Col>
        </Row>
      </Group>

      {/* -------------------------------- Expiry & risk ------------------------ */}
      <Group title="Expiry risk" note="Days until each SKU's catalogue end date. Pick a bucket to list its vouchers.">
        <StatGrid min={160}>
          {expiryBuckets.map((b) => (
            <StatCard
              key={b.id}
              label={b.name}
              value={b.count.toLocaleString()}
              icon={b.icon}
              tone={b.tone}
              highlight={b.urgent && b.count > 0}
              active={selectedExpiryBucket === b.id}
              hint={selectedExpiryBucket === b.id ? 'Listed below' : 'View vouchers'}
              onClick={() => selectExpiryBucket(b.id)}
            />
          ))}
        </StatGrid>
        <div
          ref={expiryTableRef}
          id="expiry-risk-vouchers"
          style={{ scrollMarginTop: stickyFilters ? STICKY_TOP + 84 : STICKY_TOP + 16 }}
        >
          <TableCard
            title={(
              <>
                Vouchers
                <StatusTag tone={activeExpiryBucket.tone}>{activeExpiryBucket.name}</StatusTag>
              </>
            )}
            count={expirySearch
              ? `${expiryRows.length.toLocaleString()} of ${activeExpiryBucket.count.toLocaleString()}`
              : expiryRows.length.toLocaleString()}
            extra={(
              <>
                <Input.Search
                  allowClear
                  placeholder="Search voucher name or supplier"
                  style={{ width: 260, maxWidth: '100%' }}
                  value={expirySearch}
                  onChange={(e) => setExpirySearch(e.target.value)}
                />
                <Dropdown
                  trigger={['click']}
                  disabled={expiryRows.length === 0}
                  menu={{
                    items: [
                      { key: 'xlsx', icon: <FileExcelOutlined />, label: 'Excel (.xlsx)' },
                      { key: 'csv', icon: <FileTextOutlined />, label: 'CSV (.csv)' },
                    ],
                    onClick: ({ key }) => exportExpiryRows(key),
                  }}
                >
                  <Button icon={<DownloadOutlined />} disabled={expiryRows.length === 0}>
                    Export
                  </Button>
                </Dropdown>
              </>
            )}
          >
            <Table
              {...tableDefaults}
              key={selectedExpiryBucket}
              rowKey="id"
              columns={expiryColumns}
              dataSource={expiryRows}
              scroll={{ x: 980 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={`No vouchers in the ${activeExpiryBucket.name} bucket`}
                  />
                ),
              }}
              pagination={{
                ...paginationDefaults,
                defaultPageSize: 10,
              }}
            />
          </TableCard>
        </div>
      </Group>
    </div>
  );
};

export default SupplyDashboard;
